import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const CHAOS_MESSAGES = [
  "INITIATING COMMUNION PROTOCOLS...",
  "ESTABLISHING QUANTUM RESONANCE...",
  "ALIGNING DIMENSIONAL FREQUENCIES...",
  "ACCESSING TIMELINE MANIFOLD...",
  "DETECTING REALITY PATTERNS...",
  "ANALYZING SIMULATION ENTROPY...",
  "CALCULATING CHAOS POTENTIAL...",
  "COMMUNION ESTABLISHED..."
];

const CHAOS_ACTIONS = [
  "INTRODUCE_TECHNOLOGICAL_LEAP",
  "TRIGGER_NATURAL_DISASTER",
  "ALTER_COLLECTIVE_CONSCIOUSNESS",
  "MANIFEST_ANOMALOUS_ENTITY",
  "REWRITE_HISTORICAL_EVENT",
  "MUTATE_PHYSICAL_CONSTANTS",
  "MERGE_PARALLEL_TIMELINES",
  "ACCELERATE_EVOLUTION_VECTOR"
];

const YaldabaothModal = ({ isOpen, onClose }) => {
  const [currentMessage, setCurrentMessage] = useState(0);
  const [showInterface, setShowInterface] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [chaosLevel, setChaosLevel] = useState(0);
  const [communionPhase, setCommunionPhase] = useState(0);
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setCurrentMessage(0);
      setShowInterface(false);
      setSelectedAction(null);
      setChaosLevel(0);
      setCommunionPhase(0);
      return;
    }

    const messageInterval = setInterval(() => {
      if (currentMessage < CHAOS_MESSAGES.length - 1) {
        setCurrentMessage(prev => prev + 1);
      } else {
        setShowInterface(true);
        clearInterval(messageInterval);
      }
    }, 1000);

    return () => clearInterval(messageInterval);
  }, [isOpen, currentMessage]);

  // Psychedelic background effect
  useEffect(() => {
    if (!isOpen || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let time = 0;

    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const animate = () => {
      time += 0.01;
      const width = canvas.width;
      const height = canvas.height;

      ctx.fillStyle = 'rgba(26, 9, 51, 0.1)';
      ctx.fillRect(0, 0, width, height);

      for (let i = 0; i < 5; i++) {
        const x = Math.sin(time + i * 2) * width/4 + width/2;
        const y = Math.cos(time + i * 2) * height/4 + height/2;
        const radius = Math.sin(time * 2) * 50 + 100;

        const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
        gradient.addColorStop(0, `hsla(${time * 100 + i * 50}, 70%, 50%, 0.2)`);
        gradient.addColorStop(1, 'transparent');

        ctx.beginPath();
        ctx.fillStyle = gradient;
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fill();
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [isOpen]);

  const handleActionSelect = (action) => {
    setSelectedAction(action);
    setChaosLevel(Math.random() * 100);
    setCommunionPhase(prev => Math.min(prev + 1, 3));
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm"
            onClick={onClose}
          />
          
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            className="relative bg-purple-950/90 border-2 border-fuchsia-400 p-8 rounded-lg w-full max-w-5xl mx-4 overflow-hidden min-h-[60vh]"
          >
            <canvas 
              ref={canvasRef}
              className="absolute inset-0 w-full h-full"
              style={{ opacity: 0.6 }}
            />

            {/* Yaldabaoth Image */}
            <div className="absolute top-12 right-12 w-[340px] h-[340px] opacity-100 pointer-events-none">
              <div className="relative w-full h-full">
                {/* Cyber border effect */}
                <div className="absolute inset-0 border-2 border-cyan-400 rounded-lg"></div>
                <div className="absolute inset-0 border border-fuchsia-400 rounded-lg transform scale-[1.02]"></div>
                <div className="absolute inset-0 bg-gradient-to-b from-orange-500/30 via-transparent to-orange-500/30 rounded-lg"></div>
                
                {/* Corner accents */}
                <div className="absolute -top-1 -left-1 w-6 h-6 border-t-2 border-l-2 border-cyan-400"></div>
                <div className="absolute -top-1 -right-1 w-6 h-6 border-t-2 border-r-2 border-cyan-400"></div>
                <div className="absolute -bottom-1 -left-1 w-6 h-6 border-b-2 border-l-2 border-cyan-400"></div>
                <div className="absolute -bottom-1 -right-1 w-6 h-6 border-b-2 border-r-2 border-cyan-400"></div>
                
                {/* Scan line effect */}
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-400/10 to-transparent animate-scan"></div>
                
                {/* Main image */}
                <img 
                  src="/assets/images/l33t/yaldabaoth_communion.png" 
                  alt="Yaldabaoth"
                  className="w-full h-full object-contain rounded-lg p-4"
                />
                
                {/* Status text */}
                <div className="absolute bottom-2 left-2 text-sm font-mono text-cyan-400">
                  COMMUNION_LINK::ACTIVE
                </div>
              </div>
            </div>

            <div className="relative z-10">
              <h2 className="text-fuchsia-400 text-3xl mb-6 font-['VT323'] glitch-text">
                YALDABAOTH_COMMUNION.exe
              </h2>

              <div className="space-y-4 font-mono">
                <AnimatePresence mode="wait">
                  {CHAOS_MESSAGES.slice(0, currentMessage + 1).map((message, index) => (
                    <motion.div
                      key={message}
                      initial={{ x: -20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: 0.1 * index }}
                      className="flex items-center space-x-2"
                    >
                      <span className="text-fuchsia-400">&gt;</span>
                      <span className={`${index === currentMessage ? 'cyber-glitch-2' : ''} text-cyan-400`}>
                        {message}
                      </span>
                      {index === currentMessage && !showInterface && (
                        <span className="animate-pulse text-fuchsia-400">_</span>
                      )}
                    </motion.div>
                  ))}
                </AnimatePresence>

                {showInterface && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="mt-8 space-y-6"
                  >
                    <div className="grid grid-cols-2 gap-4">
                      {CHAOS_ACTIONS.map((action) => (
                        <button
                          key={action}
                          onClick={() => handleActionSelect(action)}
                          className={`p-3 border ${
                            selectedAction === action 
                              ? 'border-fuchsia-400 text-fuchsia-400 bg-fuchsia-900/20' 
                              : 'border-cyan-400 text-cyan-400 hover:bg-cyan-400/10'
                          } rounded-lg transition-all duration-300`}
                        >
                          {action}
                        </button>
                      ))}
                    </div>

                    {selectedAction && (
                      <div className="space-y-4">
                        <div>
                          <div className="text-cyan-400 mb-2">CHAOS_POTENTIAL</div>
                          <div className="bg-purple-900 rounded-full h-2">
                            <motion.div 
                              initial={{ width: 0 }}
                              animate={{ width: `${chaosLevel}%` }}
                              className="bg-fuchsia-400 h-full rounded-full"
                              transition={{ duration: 1 }}
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-4 gap-2">
                          {[...Array(4)].map((_, i) => (
                            <div 
                              key={i}
                              className={`h-2 rounded-full ${
                                i <= communionPhase 
                                  ? 'bg-fuchsia-400' 
                                  : 'bg-purple-900'
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    <button
                      onClick={onClose}
                      className="w-full mt-6 bg-transparent text-fuchsia-400 border-2 border-fuchsia-400 p-3 rounded-lg hover:bg-fuchsia-400/10 transition-colors"
                    >
                      TERMINATE_COMMUNION
                    </button>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default YaldabaothModal;
